import { AfterViewInit, Component, ViewChild } from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ItemCategory } from '../model/item-category';
import { EquipmentFactory } from '../model/equipment-factory';
import { EquipmentItem } from '../model/equipment-item';
import { Cost } from '../model/cost';
import { CoinType } from '../model/coin-type';

@Component({
  selector: 'app-equipment-list',
  templateUrl: './equipment-list.component.html',
  styleUrls: ['./equipment-list.component.scss']
})
export class EquipmentListComponent implements AfterViewInit {

  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(MatPaginator) paginator: MatPaginator | null;

  public equipmentTableDataSource: MatTableDataSource<EquipmentItem>;
  public categories: ItemCategory[];

  public searchString: string;
  public selectedCategory: ItemCategory;
  public selectedSubCategories: any;

  public selectedItem: EquipmentItem | null;
  public itemsToBuy: Map<EquipmentItem, number>;
  public showRollResults: boolean;

  private equipmentItems: EquipmentItem[];

  private readonly allCategoriesItemCategory = new ItemCategory(ItemCategory.All);

  public columnsToDisplay: string[] = this.allCategoriesItemCategory.validFields;

  constructor() {
    this.sort = new MatSort();
    this.paginator = null;
    this.selectedCategory = this.allCategoriesItemCategory;
    this.selectedSubCategories = undefined;
    this.selectedItem = null;
    this.searchString = '';
    this.itemsToBuy = new Map<EquipmentItem, number>();

    this.equipmentItems = EquipmentFactory.getAllEquipment();
    this.showRollResults = false;

    this.categories = this.getEquipmentCategories();

    this.equipmentTableDataSource = new MatTableDataSource<EquipmentItem>(this.equipmentItems);
  }

  public get itemsToBuyTotalCost(): Cost | undefined {
    if (!this.itemsToBuy) {
      return undefined;
    }

    var valuesInBits = Array.from(this.itemsToBuy).map(([key, value]) => new Cost(key.cost.valueInBits * value, CoinType.bit));
    return Cost.sum(valuesInBits);
  }

  public get allCategoriesShown(): boolean {
    return this.selectedCategory.name == ItemCategory.All;
  }

  public ngAfterViewInit(): void {
    this.equipmentTableDataSource.sortingDataAccessor = (item: EquipmentItem, sortHeaderId: string) => {
      var itemAsAny: any = item;

      switch (sortHeaderId) {
        case 'cost': return item.cost.valueInBits;
        case 'rating': return item.rating;
        default: return itemAsAny[sortHeaderId];
      }
    };

    this.equipmentTableDataSource.sort = this.sort;
    this.equipmentTableDataSource.paginator = this.paginator;
    this.equipmentTableDataSource.filterPredicate = EquipmentListComponent.filterPredicate;
    this.updateFilterCriteria();
  }

  public onCategoryChange() {
    this.selectedSubCategories = new Object();
    this.selectedCategory.subCategories.forEach(sc => this.selectedSubCategories[sc] = true);

    this.updateFilterCriteria();

    this.columnsToDisplay = this.selectedCategory.validFields;
  }

  public onClickAllSubCategories() {
    this.selectedCategory.subCategories.forEach(sc => this.selectedSubCategories[sc] = true);

    this.updateFilterCriteria();
  }

  public onClickNoSubCategories() {
    this.selectedCategory.subCategories.forEach(sc => this.selectedSubCategories[sc] = false);

    this.updateFilterCriteria();
  }

  public onSubCategoryChange(subCategory: string, event: MatCheckboxChange) {
    this.selectedSubCategories[subCategory] = event.checked;

    this.updateFilterCriteria();
  }

  public onSearchStringChange(event: Event) {
    var target = event.target as any;

    if (!target) {
      return;
    }

    this.searchString = target.value ?? '';
    this.updateFilterCriteria();
  }

  public selectItem(item: EquipmentItem) {
    this.selectedItem = item;
    this.showRollResults = false;
  }

  public addItemToBuy(item: EquipmentItem) {
    if (!this.itemsToBuy) {
      return;
    }

    const count = this.itemsToBuy.get(item);
    if (count == undefined) {
      this.itemsToBuy.set(item, 1);
    }
    else {
      this.itemsToBuy.set(item, count + 1);
    }
  }

  public removeItemToBuy(item: EquipmentItem) {
    if (!this.itemsToBuy) {
      return;
    }

    const count = this.itemsToBuy.get(item);
    if (count == undefined) {
      return;
    }
    if (count > 1) {
      this.itemsToBuy.set(item, count - 1);
    }
    else {
      this.itemsToBuy.delete(item);
    }
  }

  public rollAvailability(item: EquipmentItem, bonus = 0) {
    const percentChance = item.availability + bonus;
    const roll = Math.floor(Math.random() * 100) + 1;

    item.isAvailable = roll <= percentChance;

    item.availabilityRollResults = bonus === 0
      ? `Rolled ${roll}`
      : `Rolled ${roll} vs ${percentChance} (${item.availability} + ${bonus})`;

    this.showRollResults = true;
  }

  private getEquipmentCategories(): ItemCategory[] {
    let categories: ItemCategory[] = [];

    this.equipmentItems.forEach(item => {
      var category = categories.find(cat => cat.name == item.category);

      if (!category) {
        category = new ItemCategory(item.category);
        categories.push(category);
      }

      if (!category.subCategories.includes(item.subCategory)) {
        category.subCategories.push(item.subCategory);
        category.subCategories.sort();
      }
    });

    categories.sort((a, b) => a.name.localeCompare(b.name));

    return [this.allCategoriesItemCategory].concat(categories);
  }

  private updateFilterCriteria() {
    var selectedSubCategories = this.selectedCategory.subCategories.filter(sc => this.selectedSubCategories[sc] === true);
    var filterCriteria = new FilterCriteria(this.searchString, this.selectedCategory.name, selectedSubCategories);
    this.equipmentTableDataSource.filter = JSON.stringify(filterCriteria);
  }

  private static filterPredicate(item: EquipmentItem, filter: string): boolean {
    if (!filter) {
      return true;
    }

    const filterCriteria: FilterCriteria = JSON.parse(filter);

    categorySearchMatches = false;
    if (filterCriteria.category == ItemCategory.All) {
      categorySearchMatches = true;
    }
    else {
      const categoryMatches = filterCriteria.category == item.category;
      const subCategoryMatches = filterCriteria.subCategories.includes(item.subCategory);
      var categorySearchMatches = categoryMatches && subCategoryMatches;
    }

    var textSearchMatches = false;
    if (!filterCriteria.searchText) {
      textSearchMatches = true;
    } else {
      var itemNameLower = item.name.toLowerCase();
      var searchTextLower = filterCriteria.searchText.toLowerCase();

      textSearchMatches = itemNameLower.includes(searchTextLower);
    }

    return categorySearchMatches && textSearchMatches;
  }
}

class FilterCriteria {
  constructor(public searchText: string, public category: string, public subCategories: string[]) {
  }
}
